import { useState, useEffect, useCallback } from 'react'
import { Settings as SettingsIcon, BookOpen } from 'lucide-react'
import { useHighlightsApi } from './useHighlightsApi'
import { useCurrentProfile } from './useCurrentProfile'
import CaptionLibrary from './CaptionLibrary'
import TranscriptSearch from './TranscriptSearch'
import HighlightReelBuilder from './HighlightReelBuilder'
import KeyMomentsExplorer from './KeyMomentsExplorer'
import SettingsEditor from './SettingsEditor'
import AiDocsModal from './AiDocsModal'
import ReelTransitions from './ReelTransitions'  // dev25: dedicated tab
import PromptsWorkbench from './PromptsWorkbench'  // Phase 5.2

const TABS = [
  { id: 'library',     label: 'Caption Library' },
  { id: 'search',      label: 'Search' },
  { id: 'key-moments', label: 'Key Moments' },
  { id: 'reels',       label: 'Reels' },
  { id: 'transitions', label: 'Reel Transitions' },  // dev25
  { id: 'prompts',     label: 'Prompts' },           // Phase 5.2
]

// Local cart of selected (tbtv_id, start_ms) segments. Lifted to this
// component so it persists across tab switches.
function useReelCart() {
  const [cart, setCart] = useState([])
  const add = useCallback((segment) => {
    setCart(prev => {
      // Dedupe identical (tbtv_id, start_ms) pairs.
      if (prev.some(s => s.tbtv_id === segment.tbtv_id && s.start_ms === segment.start_ms)) {
        return prev
      }
      return [...prev, segment]
    })
  }, [])
  const remove = useCallback((tbtv_id, start_ms) => {
    setCart(prev => prev.filter(s => !(s.tbtv_id === tbtv_id && s.start_ms === start_ms)))
  }, [])
  const clear = useCallback(() => setCart([]), [])
  const has = useCallback((tbtv_id, start_ms) =>
    cart.some(s => s.tbtv_id === tbtv_id && s.start_ms === start_ms), [cart])
  return { cart, add, remove, clear, has }
}

export default function TbtvHighlights() {
  const [tab, setTab] = useState('library')
  const [health, setHealth] = useState(null)
  const [healthErr, setHealthErr] = useState(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [aiDocsOpen, setAiDocsOpen] = useState(false)
  const api = useHighlightsApi()
  const cart = useReelCart()
  // Phase 3: active sport profile drives the chip vocabulary in the Search
  // and Key Moments tabs. Resolved from URL ?profile= → localStorage →
  // is_default profile → 'hockey'. Hockey is the only enabled profile
  // today (Phase 5 adds football), so the dropdown will show one option
  // until then and the whole UI stays exactly as it is.
  const { profileKey, setProfile, profiles, loading: profilesLoading } = useCurrentProfile()

  useEffect(() => {
    let cancelled = false
    api.health()
      .then(res => { if (!cancelled) setHealth(res) })
      .catch(err => { if (!cancelled) setHealthErr(err.message) })
    return () => { cancelled = true }
  }, [])

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ marginBottom: 4 }}>Highlights</h2>
          <p className="muted" style={{ margin: 0 }}>
            Caption-based search and clip-reel builder for the on-demand content library.
          </p>
        </div>
        {/* Sport profile selector — chip vocabulary + AI dispatch source. */}
        {/* Only render when > 1 profile is enabled; otherwise it's noise. */}
        {profiles.length > 1 && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted, #888)' }}>
            <span>Profile</span>
            <select
              value={profileKey}
              onChange={e => setProfile(e.target.value)}
              disabled={profilesLoading}
              style={{
                background: 'var(--bg-secondary, #1a1a1a)',
                color: 'var(--text-primary, #fff)',
                border: '1px solid #333',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: 12,
                cursor: profilesLoading ? 'wait' : 'pointer',
              }}
              title="Which sport profile drives the chip vocabulary + AI dispatch on this page"
            >
              {profiles.map(p => (
                <option key={p.profile_key} value={p.profile_key}>{p.display_name}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {(healthErr || (health && health.db && !health.db.ok)) && (
        <div className="card" style={{ marginBottom: 16, background: '#2a1414', border: '1px solid #5a1f1f' }}>
          <strong style={{ color: '#fca5a5' }}>Worker connectivity issue</strong>
          {healthErr && (
            <pre style={{ color: 'crimson', whiteSpace: 'pre-wrap', margin: '4px 0 0', fontSize: 12 }}>{healthErr}</pre>
          )}
          {health && health.db && !health.db.ok && (
            <pre style={{ margin: '4px 0 0', fontSize: 12 }}>{JSON.stringify(health, null, 2)}</pre>
          )}
        </div>
      )}

      <div className="tab-bar" style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid var(--border-color, #2a2a2a)', alignItems: 'center' }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === t.id ? '2px solid var(--accent, #4a90e2)' : '2px solid transparent',
              color: tab === t.id ? 'var(--text-primary, #fff)' : 'var(--text-muted, #888)',
              cursor: 'pointer',
              fontWeight: tab === t.id ? 600 : 400,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {t.label}
            {t.id === 'reels' && cart.cart.length > 0 && (
              <span style={{ background: '#3b82f6', color: 'white', padding: '0 6px', borderRadius: 10, fontSize: 11, fontWeight: 600, minWidth: 18, textAlign: 'center' }}>
                {cart.cart.length}
              </span>
            )}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setAiDocsOpen(true)}
          title="About the AI methods — what each detector does, costs, and current status"
          style={{
            padding: '6px 10px',
            background: 'transparent',
            border: '1px solid #333',
            borderRadius: 4,
            color: 'var(--text-muted, #888)',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
          }}
        >
          <BookOpen size={14} /> About AI
        </button>
        <button
          onClick={() => setSettingsOpen(true)}
          title="Module settings — toggle Mux Robots auto-triggers"
          style={{
            padding: '6px 10px',
            background: 'transparent',
            border: '1px solid #333',
            borderRadius: 4,
            color: 'var(--text-muted, #888)',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
          }}
        >
          <SettingsIcon size={14} /> Settings
        </button>
      </div>

      {tab === 'library'     && <CaptionLibrary />}
      {tab === 'search'      && <TranscriptSearch reelCart={cart} onSwitchTo={setTab} profileKey={profileKey} profiles={profiles} />}
      {tab === 'key-moments' && <KeyMomentsExplorer reelCart={cart} profileKey={profileKey} profiles={profiles} />}
      {tab === 'reels'       && <HighlightReelBuilder reelCart={cart} />}
      {tab === 'transitions' && <ReelTransitions />}
      {tab === 'prompts'     && <PromptsWorkbench profiles={profiles} initialProfileKey={profileKey} />}

      {settingsOpen && <SettingsEditor onClose={() => setSettingsOpen(false)} />}
      {aiDocsOpen && <AiDocsModal onClose={() => setAiDocsOpen(false)} />}
    </div>
  )
}
